import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiCalendar, FiX, FiArrowLeft } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';

const Namuna2Info = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('list'); // 'list' or 'form'
    
    //---------------------------------------------------------
    // DATA STATE
    //---------------------------------------------------------
    const [records, setRecords] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [listSearchTerm, setListSearchTerm] = useState('');

    //---------------------------------------------------------
    // FORM STATE
    //---------------------------------------------------------
    const initialFormState = {
        resolutionNo: '',
        resolutionDate: '',
        transactionType: 'jama', // 'jama' or 'kharch'
        selectedAccount: null,
        sanctionedAmount: '',
        newAmount: '',
        diffAmount: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [transactions, setTransactions] = useState([]);
    
    // Edit State
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);

    // Report State
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [selectedReportRecord, setSelectedReportRecord] = useState(null);
    
    //---------------------------------------------------------
    // FETCH DATA
    //---------------------------------------------------------
    useEffect(() => {
        // Fetch Namuna 2 Records
        const recordsRef = ref(database, 'Grampanchayat/Panipuravtha/Namuna2');
        const unsubscribeRecords = onValue(recordsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadedRecords = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // Sort by date descending if needed, currently just loading
                setRecords(loadedRecords);
            } else {
                setRecords([]);
            }
        });

        // Fetch Accounts for Modal
        const accountsRef = ref(database, 'Grampanchayat/Panipuravtha/AccountDetails');
        const unsubscribeAccounts = onValue(accountsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const loadedAccounts = Object.keys(data).map(key => ({
                    id: key, // Firebase Key
                    ...data[key] // Contains accountNo, accountName, accountGroupName etc.
                }));
                setAccounts(loadedAccounts);
            } else {
                setAccounts([]);
            }
        });

        return () => {
            unsubscribeRecords();
            unsubscribeAccounts();
        };
    }, []);

    //---------------------------------------------------------
    // HANDLERS
    //---------------------------------------------------------
    const handleAddClick = () => {
        setFormData(initialFormState);
        setTransactions([]);
        setIsEditing(false);
        setSelectedId(null);
        setView('form');
    };

    const handleBackClick = () => {
        setView('list');
        setIsEditing(false);
        setSelectedId(null);
    };

    const handleAccountSelect = (acc) => {
        setFormData(prev => ({ 
            ...prev, 
            selectedAccount: {
                id: acc.accountNo, // Using manual account no from AccountDetails
                name: acc.accountName,
                group: acc.accountGroupName
            },
            sanctionedAmount: '0' // Default 0 as per requirement
        }));
        setModalOpen(false);
    };

    const handleTransactionAdd = () => {
        if (!formData.selectedAccount || !formData.newAmount) {
            alert('खाते आणि रक्कम निवडा');
            return;
        }

        const newTx = {
            id: Date.now(),
            type: formData.transactionType,
            accountId: formData.selectedAccount.id,
            accountName: formData.selectedAccount.name,
            sanctionedAmount: formData.sanctionedAmount || 0,
            newAmount: formData.newAmount,
            diffAmount: formData.diffAmount || 0
        };

        setTransactions([...transactions, newTx]);
        
        // Reset transaction inputs
        setFormData(prev => ({
            ...prev,
            selectedAccount: null,
            sanctionedAmount: '',
            newAmount: '',
            diffAmount: ''
        }));
    };

    const handleSave = () => {
        if (!formData.resolutionNo || !formData.resolutionDate) {
            alert("कृपया ठराव क्र. आणि दिनांक भरा");
            return;
        }

        if (transactions.length === 0) {
            alert("किमान एक व्यवहार जोडा");
            return;
        }

        const recordData = {
            date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            resolutionNo: formData.resolutionNo,
            resolutionDate: formData.resolutionDate,
            departmentId: 'पाणीपुरवठा',
            transactions: transactions,
            updatedAt: Date.now()
        };

        if (isEditing && selectedId) {
            const recordRef = ref(database, `Grampanchayat/Panipuravtha/Namuna2/${selectedId}`);
            update(recordRef, recordData)
                .then(() => {
                    alert("Data Updated Successfully!");
                    setView('list');
                    setFormData(initialFormState);
                    setIsEditing(false);
                    setSelectedId(null);
                })
                .catch((error) => {
                    alert("Error updating data: " + error.message);
                });
        } else {
            const newRecord = {
                 ...recordData,
                 createdAt: Date.now()
            };
            const recordsRef = ref(database, 'Grampanchayat/Panipuravtha/Namuna2');
            push(recordsRef, newRecord)
                .then(() => {
                    alert("Data Saved Successfully!");
                    setView('list');
                    setFormData(initialFormState);
                })
                .catch((error) => {
                    alert("Error saving data: " + error.message);
                });
        }
    };

    const handleEdit = (rec) => {
        setFormData({
            ...initialFormState,
            resolutionNo: rec.resolutionNo,
            resolutionDate: rec.resolutionDate,
        });
        setTransactions(rec.transactions || []);
        setIsEditing(true);
        setSelectedId(rec.id);
        setView('form');
    };

    const handleDelete = (id) => {
        if (window.confirm("तुम्ही नक्की हे रेकॉर्ड खोडू इच्छिता का?")) {
            const recordRef = ref(database, `Grampanchayat/Panipuravtha/Namuna2/${id}`);
            remove(recordRef)
                .then(() => alert("Record deleted successfully"))
                .catch(err => alert("Error deleting: " + err.message));
        }
    };

    const handleReportClick = (rec) => {
        setSelectedReportRecord(rec);
        setIsReportOpen(true);
        // Auto-print after a short delay to ensure rendering
        setTimeout(() => {
            window.print();
        }, 500);
    };

    const closeReport = () => {
        setIsReportOpen(false);
        setSelectedReportRecord(null);
    };

    //---------------------------------------------------------
    // RENDER HELPERS
    //---------------------------------------------------------
    const filteredAccounts = accounts.filter(acc => 
        (acc.accountName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
        (acc.accountNo?.toString() || '').includes(searchTerm)
    );

    const filteredRecords = records.filter(rec => 
        (rec.resolutionNo?.toLowerCase() || '').includes(listSearchTerm.toLowerCase()) ||
        (rec.departmentId?.toLowerCase() || '').includes(listSearchTerm.toLowerCase())
    );

    // If report is open, show report view
    if (isReportOpen && selectedReportRecord) {
        return (
            <div className="min-h-screen bg-white p-8 font-poppins text-black relative">
                 <style>{`
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #report-container, #report-container * {
                            visibility: visible;
                        }
                        #report-container {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            margin: 0;
                            padding: 20px;
                            background: white; 
                        }
                        .print-hidden {
                            display: none !important;
                        }
                    }
                `}</style>

                <div className="flex justify-end gap-4 mb-8 print-hidden">
                    <button 
                        onClick={() => window.print()}
                        className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                    >
                        Print Report
                    </button>
                    <button 
                        onClick={closeReport}
                        className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 font-medium"
                    >
                        Back
                    </button>
                </div>

                <div id="report-container" className="max-w-4xl mx-auto border border-black p-8 print:border-0 print:p-0">
                    <div className="text-center mb-6">
                        <h1 className="text-xl font-bold">नमुना नं. २</h1>
                        <h2 className="text-lg font-bold underline">पुनर्विनियोजन व नियतवाटप</h2>
                         <p className="mt-2">ठराव क्रमांक: {selectedReportRecord.resolutionNo} &nbsp;&nbsp;&nbsp; दिनांक: {selectedReportRecord.date}</p>
                    </div>

                    <table className="w-full border-collapse border border-black text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border border-black p-2">अ.क्र.</th>
                                <th className="border border-black p-2">प्रकार</th>
                                <th className="border border-black p-2">खातावणी</th>
                                <th className="border border-black p-2">मंजुर रक्कम</th>
                                <th className="border border-black p-2">नवीन रक्कम</th>
                                <th className="border border-black p-2">फरक (अधिक/कमी)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedReportRecord.transactions?.map((tx, idx) => (
                                <tr key={idx}>
                                    <td className="border border-black p-2 text-center">{idx + 1}</td>
                                    <td className="border border-black p-2 text-center">{tx.type === 'jama' ? 'जमा' : 'खर्च'}</td>
                                    <td className="border border-black p-2">{tx.accountName}</td>
                                    <td className="border border-black p-2 text-right">{tx.sanctionedAmount}</td>
                                    <td className="border border-black p-2 text-right">{tx.newAmount}</td>
                                    <td className="border border-black p-2 text-right">{tx.diffAmount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                     <div className="mt-16 flex justify-between px-10 font-bold">
                        <div>हिशोब तपासनीस</div>
                        <div>सरपंच / ग्रामसेवक</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6 font-poppins">
            
            {/* -------------------- LIST VIEW -------------------- */}
            {view === 'list' && (
                <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-blue-900">
                    <div className="bg-blue-800 text-white p-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)} 
                            className="hover:bg-blue-700 p-1 rounded-full transition-colors flex items-center justify-center"
                        >
                            <FiArrowLeft className="text-xl" />
                        </button>
                        <h2 className="text-xl font-medium">नमुना २ पुनर्विनियोजन व नियतवाटप</h2>
                    </div>
                     <button 
                        onClick={handleAddClick}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-1 rounded shadow"
                    >
                        नविन
                    </button>
                </div>
                    
                    <div className="p-4 bg-white border-b border-gray-200 flex justify-between items-center gap-4">
                        <div className="relative flex-1">
                            <input 
                                type="text" 
                                placeholder="शोधणे (ठराव क्र. / विभाग)" 
                                value={listSearchTerm}
                                onChange={(e) => setListSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded text-gray-700 focus:outline-blue-500"
                            />
                            <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        </div>
                        <button className="bg-green-600 text-white px-6 py-2 rounded">
                            शोधणे
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-center">
                            <thead className="bg-blue-800 text-white">
                                <tr>
                                    <th className="p-3 border-r border-blue-700">क्रमांक</th>
                                    <th className="p-3 border-r border-blue-700">दिनांक</th>
                                    <th className="p-3 border-r border-blue-700">ठराव नं.</th>
                                    <th className="p-3 border-r border-blue-700">ठराव दिनांक</th>
                                    <th className="p-3 border-r border-blue-700">DepartmentID</th>
                                    <th className="p-3">पर्याय</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecords.length > 0 ? filteredRecords.map((rec, index) => (
                                    <tr key={rec.id} className="border-b hover:bg-gray-50">
                                        <td className="p-3 border-r">{index + 1}</td>
                                        <td className="p-3 border-r">{rec.date}</td>
                                        <td className="p-3 border-r">{rec.resolutionNo}</td>
                                        <td className="p-3 border-r">{rec.resolutionDate}</td>
                                        <td className="p-3 border-r">{rec.departmentId}</td>
                                        <td className="p-3 flex justify-center gap-2">
                                            <button 
                                                onClick={() => handleEdit(rec)}
                                                className="bg-blue-700 text-white px-3 py-1 rounded hover:bg-blue-800"
                                            >
                                                बदल
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(rec.id)}
                                                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                                            >
                                                खोडणे
                                            </button>
                                            <button 
                                                onClick={() => handleReportClick(rec)}
                                                className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                                            >
                                                रिपोर्ट
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="p-6 text-gray-500">No records found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination Mock */}
                    <div className="bg-blue-900 p-2 flex justify-center gap-2 text-white">
                         <span className="bg-blue-700 px-3 py-1 rounded cursor-pointer">1</span>
                    </div>
                </div>
            )}


            {/* -------------------- FORM VIEW -------------------- */}
            {view === 'form' && (
                <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-blue-900">
                    <div className="bg-blue-800 text-white p-3 px-6 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                             <button onClick={handleBackClick} className="hover:bg-blue-700 p-1 rounded-full transition-colors flex items-center justify-center">
                                <FiArrowLeft className="text-xl" />
                             </button>
                             <h2 className="text-lg font-medium">नमुना २ पुनर्विनियोजन व नियतवाटप माहिती {isEditing ? '(सुधारणा)' : ''}</h2>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Header Inputs */}
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4 mb-6">
                            <div className="flex items-center">
                                <label className="w-32 text-gray-700 font-medium">क्रमांक:</label>
                                <input type="text" value={isEditing ? "Editing..." : "Auto Generated"} disabled className="flex-1 p-2 border rounded bg-gray-100 text-gray-500" />
                            </div>
                            <div className="flex items-center">
                                <label className="w-32 text-gray-700 font-medium">दिनांक:</label>
                                <div className="relative flex-1">
                                    <input type="date" defaultValue="2025-04-01" className="w-full p-2 border rounded" />
                                </div>
                            </div>
                             <div className="flex items-center">
                                <label className="w-32 text-gray-700 font-medium">ठराव नं.:</label>
                                <input 
                                    type="text" 
                                    value={formData.resolutionNo} 
                                    onChange={(e) => setFormData({...formData, resolutionNo: e.target.value})}
                                    placeholder="2" 
                                    className="flex-1 p-2 border rounded" 
                                />
                            </div>
                            <div className="flex items-center">
                                <label className="w-32 text-gray-700 font-medium">ठराव दिनांक:</label>
                                <div className="relative flex-1">
                                    <input 
                                        type="date" 
                                        value={formData.resolutionDate}
                                        onChange={(e) => setFormData({...formData, resolutionDate: e.target.value})}
                                        className="w-full p-2 border rounded" 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Transaction Section */}
                        <div className="border border-blue-200 rounded mb-6 overflow-hidden">
                            <h3 className="bg-blue-800 text-white p-2 px-4 text-sm font-medium">सविस्तर माहिती</h3>
                            
                            <div className="p-4 space-y-4">
                                {/* Row 1: Type */}
                                <div className="flex items-center gap-4">
                                    <label className="w-24 font-medium text-gray-700">प्रकार:</label>
                                    <div className="flex items-center gap-6">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="type" 
                                                checked={formData.transactionType === 'jama'} 
                                                onChange={() => setFormData({...formData, transactionType: 'jama'})}
                                                className="w-4 h-4 text-blue-600"
                                            /> 
                                            <span className="text-gray-700">जमा</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                             <input 
                                                type="radio" 
                                                name="type" 
                                                checked={formData.transactionType === 'kharch'} 
                                                onChange={() => setFormData({...formData, transactionType: 'kharch'})}
                                                className="w-4 h-4 text-blue-600"
                                            /> 
                                            <span className="text-gray-700">खर्च</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Row 2: Account and Sanctioned */}
                                <div className="flex items-center gap-4">
                                    <label className="w-24 font-medium text-gray-700">खातावणी :</label>
                                    
                                    <div className="flex items-center gap-2 flex-1">
                                        <input 
                                            type="text" 
                                            value={formData.selectedAccount?.id || ''} 
                                            readOnly 
                                            className="w-24 p-2 border rounded bg-gray-100 text-center" 
                                            placeholder="587"
                                        />
                                        <button 
                                            onClick={() => setModalOpen(true)}
                                            className="p-2 bg-blue-900 text-white rounded px-3 hover:bg-blue-800"
                                        >
                                            ...
                                        </button>
                                        <input 
                                            type="text" 
                                            value={formData.selectedAccount?.name || ''} 
                                            readOnly 
                                            className="flex-1 p-2 border rounded bg-gray-100" 
                                            placeholder="पाणी शुद्धीकरण"
                                        />
                                    </div>

                                    <div className="flex items-center gap-2 ml-4">
                                        <label className="text-gray-700 font-medium whitespace-pre-line text-right leading-tight">
                                            मंजुर <br/> रक्कम :
                                        </label>
                                        <input 
                                            type="text" 
                                            value={formData.sanctionedAmount || '0'} 
                                            disabled 
                                            className="w-32 p-2 border rounded bg-gray-200 text-gray-600" 
                                        />
                                    </div>
                                </div>

                                {/* Row 3: New Amount, Difference, Add Button */}
                                <div className="flex items-center gap-4 mt-6">
                                    <label className="w-24 font-medium text-gray-700">
                                        नविन <br/> रक्कम :
                                    </label>
                                    
                                    <input 
                                        type="text" 
                                        placeholder="5000"
                                        value={formData.newAmount}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const sank = parseFloat(formData.sanctionedAmount) || 0;
                                            const neu = parseFloat(val) || 0;
                                            const diff = neu - sank;
                                            const diffText = diff > 0 ? `${diff} अधिक (+)` : diff < 0 ? `${Math.abs(diff)} कमी (-)` : '0';
                                            
                                            setFormData({
                                                ...formData, 
                                                newAmount: val,
                                                diffAmount: diffText
                                            });
                                        }}
                                        className="w-48 p-2 border rounded" 
                                    />

                                    <div className="flex-1 text-center font-medium text-gray-700">
                                        {formData.selectedAccount && (
                                            <span>
                                                अधिक/कमी : {formData.diffAmount}
                                            </span>
                                        )}
                                    </div>

                                    <button 
                                        onClick={handleTransactionAdd}
                                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded shadow font-medium"
                                    >
                                        यादित भरा
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Transaction Table */}
                        <div className="border border-blue-900 rounded overflow-hidden mb-6 h-48 overflow-y-auto">
                            <table className="w-full text-center border-collapse">
                                <thead className="bg-blue-800 text-white text-sm sticky top-0">
                                    <tr>
                                        <th className="p-2 border-r border-blue-700">#</th>
                                        <th className="p-2 border-r border-blue-700">प्रकार</th>
                                        <th className="p-2 border-r border-blue-700">खातावणी क्र.</th>
                                        <th className="p-2 border-r border-blue-700">खातावणी</th>
                                        <th className="p-2 border-r border-blue-700">मंजुर रक्कम</th>
                                        <th className="p-2 border-r border-blue-700">नविन रक्कम</th>
                                        <th className="p-2">अधिक/कमी</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.length > 0 ? transactions.map((tx, idx) => (
                                        <tr key={tx.id} className="border-b text-sm even:bg-white odd:bg-gray-50">
                                            <td className="p-2 border-r">{idx + 1}</td>
                                            <td className="p-2 border-r">{tx.type === 'jama' ? 'जमा' : 'खर्च'}</td>
                                            <td className="p-2 border-r">{tx.accountId || '-'}</td>
                                            <td className="p-2 border-r">{tx.accountName}</td>
                                            <td className="p-2 border-r">{tx.sanctionedAmount}</td>
                                            <td className="p-2 border-r">{tx.newAmount}</td>
                                            <td className="p-2">{tx.diffAmount}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="7" className="p-4 text-gray-500 text-left pl-6">No records found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Actions */}
                        <div className="bg-blue-800 p-3 -mx-6 -mb-6 flex justify-center gap-4">
                            <button onClick={handleSave} className="bg-green-600 text-white px-6 py-2 rounded shadow hover:bg-green-700 font-medium">साठविणे</button>
                            <button onClick={handleBackClick} className="bg-red-600 text-white px-6 py-2 rounded shadow hover:bg-red-700 font-medium">रद्द करणे</button>
                        </div>
                    </div>
                </div>
            )}

            {/* -------------------- ACCOUNT MODAL -------------------- */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-hidden animate-fade-in border-t-8 border-blue-800">
                        {/* Header */}
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="text-lg">खातावणी माहिती</h3>
                            <button onClick={() => setModalOpen(false)} className="hover:bg-blue-700 rounded p-1">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        
                        {/* Search */}
                        <div className="p-4 border-b">
                            <div className="mb-1 text-sm text-gray-600">शोधणे</div>
                            <input 
                                type="text" 
                                placeholder="खातावणी क्र./ खातावणीचे नाव भरा." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full p-2 border border-blue-300 rounded focus:outline-blue-600"
                            />
                        </div>

                        {/* List */}
                        <div className="h-96 overflow-y-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-blue-800 text-white text-sm sticky top-0">
                                    <tr>
                                        <th className="p-2 border-r border-blue-600 w-24">खातावणी नं.</th>
                                        <th className="p-2 border-r border-blue-600">खातावणीचे नाव</th>
                                        <th className="p-2">खाते गट</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAccounts.map((acc, index) => (
                                        <tr 
                                            key={acc.id} 
                                            onClick={() => handleAccountSelect(acc)}
                                            className={`border-b cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-100`}
                                        >
                                            <td className="p-2 border-r border-gray-200 text-center font-medium text-gray-700">{acc.accountNo}</td>
                                            <td className="p-2 border-r border-gray-200 text-gray-800">{acc.accountName}</td>
                                            <td className="p-2 text-gray-600">{acc.accountGroupName}</td>
                                        </tr>
                                    ))}
                                    {filteredAccounts.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="p-6 text-center text-gray-500">
                                                माहिती उपलब्ध नाही
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Footer Pagination Mock */}
                        <div className="bg-blue-800 p-2 flex justify-center text-white">
                            <div className="flex items-center gap-1 bg-white text-black rounded px-2">
                                <span className="p-1 cursor-pointer hover:bg-gray-200">|&lt;</span>
                                <span className="p-1 cursor-pointer hover:bg-gray-200">&lt;</span>
                                <span className="bg-blue-600 text-white px-2 rounded">1</span>
                                <span className="p-1 cursor-pointer hover:bg-gray-200">&gt;</span>
                                <span className="p-1 cursor-pointer hover:bg-gray-200">&gt;|</span>
                            </div>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
};

export default Namuna2Info;
